import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ISO_8601, utc, Moment } from 'moment';
import { distinctUntilChanged, pairwise, ReplaySubject, takeUntil } from 'rxjs';

import { coreSelectors } from 'app/core/state';
import { formatUserName } from 'app/core/utilities/user-util';
import { AppState, Timeout } from 'app/types';
import assertDefined from 'app/utils/assert-defined';
import assertNever from 'app/utils/assert-never';
import { Loggable } from 'app/utils/logging/loggable';
import User from 'entity/User';
import { SocialEventType, Presenter } from 'messages/websocket.messages';

import SocialStateMachine from '../../social-state-machine/social-state-machine';
import { socialAppSelectors } from '../../state/app';
import { mediaSelectors } from '../../state/media';
import { HeaderToastMessageService } from '../header-toast-message.service';
import { MultimediaRoomService } from '../multimedia-room.service';
import { MultimediaUserService } from '../multimedia-user.service';
import { ToasterVideoService } from '../toaster-video.service';

export enum ToastStateType {
  NONE = 'none',
  ANNOUNCE = 'announce',
  ACTIVE = 'active',
}

const isToastNow = (startTimeMoment: Moment): boolean => {
  return utc().isAfter(startTimeMoment, 'seconds');
};

/**
 * Create a moment object of when the toast will start. If the client time is too far off of the toaster's time,
 * return null.
 * @param toastStart An ISO-8601 string of when the toast will start
 * @param isPresenter Whether or not the user is the presenter
 * @returns A moment object of when the toast will start, or null if the client time is too far off of the toaster's time
 */
const createToastTime = (toastStart: string, isPresenter: boolean): Moment | null => {
  const toastTime = utc(toastStart, ISO_8601);

  if (isPresenter) {
    return toastTime;
  }

  const audienceStartTimeMoment = utc().add(6, 's');
  const timesDiff = toastTime.diff(audienceStartTimeMoment, 'seconds');

  // If the user's time is too far off from the toaster's time, we won't show the count.
  return timesDiff > 1 || timesDiff < -1 ? null : toastTime;
};

/**
 * Create a toast countdown timer.
 *
 * This will call the the onCount callback every second, and the onStart callback when the event starts.
 * @returns
 */
const createToastCountdown = ({
  onCount,
  onStart,
  startTime,
}: {
  startTime: Moment;
  onStart: () => void;
  onCount: (count: number) => void;
}): Timeout | undefined => {
  // No countdown if user's time is too far off
  if (!startTime) {
    return;
  }

  const toastIsNow = isToastNow(startTime);
  if (!toastIsNow) {
    // execute time check loop
    const toastTimer = setInterval(() => {
      if (isToastNow(startTime)) {
        clearInterval(toastTimer);

        onStart();
        return;
      }
      const count = -utc().diff(startTime, 'seconds');

      onCount(count);
    }, 1000);
    return toastTimer;
  }
};

@Injectable()
export class ToastService extends Loggable {
  private _isToastEvent = false; // A Toast event exists
  private _isUserToaster = false;
  private bannerId?: number;
  private destroyed$ = new ReplaySubject<boolean>(1);
  private endedOwnToast = false;
  private isCameraDisabled = false;
  private isGuest = false;
  private isHost = false;
  private isMicEnabled = true;
  private isVideoEnabled = true;
  private toaster?: Presenter;
  private toastStartTime?: string;
  private _toastState: ToastStateType = ToastStateType.NONE;
  private toastTimer?: Timeout;
  private user?: User;
  private isCameraReadable = true;

  public constructor(
    private toasterVideoService: ToasterVideoService,
    private headerToastMessageService: HeaderToastMessageService,
    private multimediaUserService: MultimediaUserService,
    private multimediaRoomService: MultimediaRoomService,
    private store: Store<AppState>,
  ) {
    super();
    this.setDisplayName('ToastService');

    // User state
    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ isGuest, user }) => {
        this.user = user;
        this.isGuest = isGuest;
      });

    this.store
      .select(socialAppSelectors.isHost)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isHost) => (this.isHost = isHost));

    // Media state
    this.store.select(mediaSelectors.isVideoEnabled).subscribe((enabled) => (this.isVideoEnabled = enabled));
    this.store.select(mediaSelectors.isMicEnabled).subscribe((enabled) => (this.isMicEnabled = enabled));

    this.store
      .select(mediaSelectors.isCurrentCameraReadable)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((readable) => {
        this.isCameraReadable = readable;
      });

    // Social state
    this.store
      .select(socialAppSelectors.getToaster)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((toaster) => (this.toaster = toaster));

    this.store
      .select(socialAppSelectors.getToastStart)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((toastStartTime) => (this.toastStartTime = toastStartTime));

    this.store
      .select(socialAppSelectors.isUserToaster)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserToaster) => {
        this._isUserToaster = isUserToaster;
      });

    // Start and stop of toast
    this.store
      .select(socialAppSelectors.isToastHappening)
      .pipe(distinctUntilChanged(), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([previous, isToastHappening]) => this.handleIsToastHappeningChange(previous, isToastHappening));

    // Transition from announce to active phase
    this.store
      .select(socialAppSelectors.isToastActive)
      .pipe(distinctUntilChanged(), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([previous, isToastActive]) => this.handleIsToastActiveChange(previous, isToastActive));
  }

  /**
   * @deprecated Use social state instead
   */
  public get isToastHappening(): boolean {
    return this._isToastEvent;
  }

  /**
   * @deprecated Use social state instead
   */
  public get isUserToaster(): boolean {
    return this._isUserToaster;
  }

  public get toastState(): ToastStateType {
    return this._toastState;
  }

  /**
   * Request that a toast begin
   * @returns
   */
  public async requestStartToast(): Promise<boolean> {
    if (!this.user) {
      this.error('requestStartToast: user is not defined');
      return false;
    }
    const { userId } = this.user ?? {};
    assertDefined(userId);

    // FIXME: check that the state transition happened.
    try {
      await SocialStateMachine.shared.requestStartNewToast({
        presenter: { ...this.user, isGuest: this.isGuest, userId },
        message: '',
        userId,
      });
    } catch (e) {
      this.error('Error request start toast', e);
    }

    return true;
  }

  public async requestEndToast(toasterEndedToast: boolean): Promise<boolean> {
    if (!this._isToastEvent) {
      this.warn('requestEndToast: no toast happening');
      return false;
    }
    const { userId } = this.user ?? {};
    if (!userId) {
      this.error('requestEndToast: no userId');
      return false;
    }

    try {
      await SocialStateMachine.shared.requestEndEvent({
        prevEventType: SocialEventType.TOAST,
        userId,
      });
    } catch (e) {
      this.error('Error Request end toast', e);
    }

    this.toasterVideoService.removeToaster();
    if (toasterEndedToast && this._isUserToaster) {
      this.endedOwnToast = true;
    }

    return true;
  }

  /**
   * Handle a change in toast happening state.
   * @param previous The previous value of isToastHappening
   * @param isToastHappening True if a toast is happening, false otherwise
   * @returns void
   */
  private handleIsToastHappeningChange(previous: boolean, isToastHappening: boolean) {
    this._isToastEvent = isToastHappening;

    // Toast start
    if (!previous && isToastHappening) {
      this._toastState = ToastStateType.ANNOUNCE;
      this.info('Toast start', this.toaster);
      this.startToastCountdown();
      return;
    }

    // Toast end
    if (previous && !isToastHappening) {
      this.handleToastEnded();
      this._toastState = ToastStateType.NONE;
      this.endedOwnToast = false;
      return;
    }
  }

  /**
   * Handle a change in toast active state.
   * @param previous The previous value of isToastActive
   * @param isToastHappening True if a toast is active , false otherwise
   * @returns void
   */
  private async handleIsToastActiveChange(previous: boolean, isToastActive: boolean) {
    if (!previous && isToastActive) {
      this._toastState = ToastStateType.ACTIVE;
      this.info('Toast active ', this.toaster);
      if (this._isUserToaster) {
        await this.handleToasterToastStart();
      } else {
        await this.handleAudienceToastStart();
      }
    }
  }

  private startToastCountdown() {
    if (!this._isToastEvent) {
      this.error('startToastCountdown: called when toast is not happening');
      return;
    }
    const { userId } = this.user ?? {};
    if (!userId) {
      this.error('startToastCountdown', 'User ID is not defined');
      return;
    }

    if (!this.toastStartTime) {
      this.error('startToastCountdown', 'No toast start time');
      return;
    }

    const startTime = createToastTime(this.toastStartTime, this._isUserToaster);

    if (startTime) {
      this.toastTimer = createToastCountdown({
        startTime,
        onStart: this.onToastStart,
        onCount: this._isUserToaster ? this.handleToasterCount : this.handleAudienceCount,
      });
    } else {
      this.handleAudienceCount();
    }
  }

  private onToastStart = async () => {
    const { userId } = this.user ?? {};
    if (!userId) {
      this.error('onToastStart: userId is not defined');
      return;
    }

    // Presenter
    if (!this._isUserToaster) {
      return;
    }

    try {
      await SocialStateMachine.shared.requestUpdateToastIsActive({
        toastIsActive: true,
        userId,
      });
    } catch (e) {
      this.error('Error request update toast to active', e);
    }
  };

  private getToastStatusMessage(isCountdown?: boolean, count?: number) {
    if (this._isUserToaster) {
      return;
    }
    if (!this.toaster) {
      return;
    }

    const name = formatUserName(this.toaster);
    const timingMessage = count ? `in ${count} seconds` : 'shortly';
    const toastCountdownMessage = `${name} will be making a toast ${timingMessage}.`;
    const toastInProgressMessage = `${name} is making a toast.`;

    return isCountdown ? toastCountdownMessage : toastInProgressMessage;
  }

  private handleToasterCount = (count: number): void => {
    let additionalMsg = '';
    if (count < 4 && !this.isMicEnabled) {
      additionalMsg = 'Your Microphone has been re-enabled!';
      this.multimediaUserService.userToggleAudio(); // We're forcing the user to re-enable their mic
    }
    if (!this.isCameraDisabled && count < 2 && !this.isVideoEnabled && this.isCameraReadable) {
      additionalMsg = 'Your Video has been re-enabled!';
      this.multimediaUserService.toggleVideo();
    }
    const timingMsg = count ? `in ${count} seconds` : 'shortly';
    this.bannerId = this.headerToastMessageService.pushStaticMessage(
      {
        message: `Your toast will begin ${timingMsg}. ${additionalMsg}`,
        buttonText: 'CANCEL TOAST',
        replace: this.bannerId,
      },
      this.handleToasterEndsToastClick,
    );
  };

  private handleAudienceCount = (count?: number): void => {
    const isCountdown = true;
    const message = this.getToastStatusMessage(isCountdown, count);

    if (message) {
      this.bannerId = this.headerToastMessageService.pushStaticMessage(
        { message, buttonText: this.isHost ? 'CANCEL TOAST' : undefined, replace: this.bannerId },
        this.handleHostEndsToastClick,
      );
    }
  };

  private handleToasterEndsToastClick = async (id: number): Promise<void> => {
    this.headerToastMessageService.removeMessage(id);
    this.bannerId = undefined;
    await this.requestEndToast(true);
  };

  /**
   * Handle the user ending the toast
   */
  private handleHostEndsToastClick = async (id: number): Promise<void> => {
    this.headerToastMessageService.removeMessage(id);
    this.bannerId = undefined;
    await this.requestEndToast(false);
  };

  /**
   * Handle that a toast has started, as the user performing the toast
   */
  private handleToasterToastStart = async (): Promise<void> => {
    const { userId } = this.user ?? {};
    if (!userId) {
      this.error('handleToasterToastStart: userId is not defined');
      return;
    }

    await this.toasterVideoService.addToaster(userId);

    this.bannerId = this.headerToastMessageService.pushStaticMessage(
      { message: 'YOUR TOAST HAS BEGUN', buttonText: 'END TOAST', replace: this.bannerId },
      this.handleToasterEndsToastClick,
    );

    this.multimediaRoomService.adjustSoundForToastStart();
  };

  /**
   * Handle that a toast has started, as non-toasting user.
   */
  private async handleAudienceToastStart(): Promise<void> {
    if (!this.toaster) {
      this.error('handleAudienceToastStart: no toaster found in social state');
      return;
    }
    assertDefined(this.toaster.userId);

    if (!this._isToastEvent) {
      this.error('handleAudienceToastStart: toast is not happening');
      return;
    }

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }

    const toasterName = formatUserName(this.toaster);

    const message = `${toasterName} is currently making a toast.`;
    this.bannerId = this.headerToastMessageService.pushStaticMessage(
      { message, buttonText: this.isHost ? 'END TOAST' : undefined, replace: this.bannerId },
      this.handleHostEndsToastClick,
    );

    await this.toasterVideoService.addToaster(this.toaster.userId);
    this.multimediaRoomService.adjustSoundForToastStart();
  }

  /**
   *  Handle a toast ending
   **/
  private handleToastEnded(): void {
    this.info('handleToastEnded', { state: this._toastState });
    switch (this._toastState) {
      case ToastStateType.NONE:
        break;
      case ToastStateType.ANNOUNCE:
        this.endAnnouncedToast();
        break;
      case ToastStateType.ACTIVE:
        this.endActiveToast();
        break;
      default:
        assertNever(this._toastState);
    }
  }

  /**
   * Tear down a toast that has been announced, but has not started
   */
  private endAnnouncedToast(): void {
    if (this.toastTimer) {
      clearInterval(this.toastTimer);
    }
    this.toastTimer = undefined;
    // Handle case where last-minute cancel doesn't catch in time
    this.headerToastMessageService.removeMessage(this.bannerId);
    this.bannerId = undefined;
    const message = this.endedOwnToast ? 'You have cancelled your toast.' : 'The toast has been cancelled.';
    this.headerToastMessageService.pushTemporaryMessage({ message });
  }

  /**
   * End a toast that is active.
   */
  private endActiveToast(): void {
    let message = 'You have ended your toast.';
    if (!this.endedOwnToast) {
      message = this._isUserToaster ? 'A host ended your toast.' : 'The toast has ended.';
    }

    this.headerToastMessageService.removeMessage(this.bannerId);
    this.bannerId = undefined;
    this.headerToastMessageService.pushTemporaryMessage({ message });

    if (this.toastTimer) {
      clearInterval(this.toastTimer);
    }
    this.toastTimer = undefined;

    this.toasterVideoService.removeToaster();
    this.endedOwnToast = false;
  }
}
